import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { enforceQuota, incrementUsage, QuotaExceededError } from "./lib/quota";
import { CAPABILITIES } from "@/lib/quotas";

export type LearningStatus = "todo" | "in_progress" | "done";

export type LearningItem = {
  id: string;
  skill: string;
  rationale: string | null;
  priority: number;
  status: LearningStatus;
  resource_url: string | null;
  source_application_id: string | null;
  source_jd_id: string | null;
  source_stage: string | null;
  created_at: string;
  updated_at: string;
};

export const listLearningQueue = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("learning_queue")
      .select(
        "id, skill, rationale, priority, status, resource_url, source_application_id, source_jd_id, source_stage, created_at, updated_at",
      )
      .eq("user_id", userId)
      .order("status", { ascending: true })
      .order("priority", { ascending: true })
      .order("created_at", { ascending: false });
    if (error) throw new Response(error.message, { status: 500 });
    return { items: (data ?? []) as LearningItem[] };
  });

export const addLearningItem = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: {
      skill: string;
      rationale?: string | null;
      priority?: number;
      resource_url?: string | null;
      source_application_id?: string | null;
      source_jd_id?: string | null;
      source_stage?: string | null;
    }) => {
      if (!input?.skill?.trim()) throw new Error("skill required");
      return input;
    },
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    // Dedupe by skill (case-insensitive) for the user
    const { data: existing } = await supabase
      .from("learning_queue")
      .select("id, status")
      .eq("user_id", userId)
      .ilike("skill", data.skill.trim())
      .maybeSingle();
    if (existing) {
      return { id: existing.id as string, alreadyExists: true };
    }

    // Only enforce quota for genuinely new items.
    try {
      await enforceQuota(supabase, userId, CAPABILITIES.learning_item);
    } catch (e) {
      if (e instanceof QuotaExceededError)
        throw new Response(e.message, { status: e.status });
      throw e;
    }

    const { data: ins, error } = await supabase
      .from("learning_queue")
      .insert({
        user_id: userId,
        skill: data.skill.trim(),
        rationale: data.rationale?.trim() || null,
        priority: data.priority ?? 3,
        resource_url: data.resource_url?.trim() || null,
        source_application_id: data.source_application_id ?? null,
        source_jd_id: data.source_jd_id ?? null,
        source_stage: data.source_stage ?? null,
      } as never)
      .select("id")
      .single();
    if (error) throw new Response(error.message, { status: 500 });

    await incrementUsage(userId, CAPABILITIES.learning_item);

    return { id: ins.id as string, alreadyExists: false };
  });

export const updateLearningItem = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: {
      id: string;
      status?: LearningStatus;
      priority?: number;
      resource_url?: string | null;
      rationale?: string | null;
    }) => {
      if (!input?.id) throw new Error("id required");
      return input;
    },
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const patch: Record<string, unknown> = {};
    if (data.status) patch.status = data.status;
    if (typeof data.priority === "number") patch.priority = data.priority;
    if (data.resource_url !== undefined) patch.resource_url = data.resource_url;
    if (data.rationale !== undefined) patch.rationale = data.rationale;
    const { error } = await supabase
      .from("learning_queue")
      .update(patch as never)
      .eq("id", data.id)
      .eq("user_id", userId);
    if (error) throw new Response(error.message, { status: 500 });
    return { ok: true };
  });

export const deleteLearningItem = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { id: string }) => {
    if (!input?.id) throw new Error("id required");
    return input;
  })
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("learning_queue")
      .delete()
      .eq("id", data.id)
      .eq("user_id", userId);
    if (error) throw new Response(error.message, { status: 500 });
    return { ok: true };
  });
